import { forEach } from 'lodash';
import { Platform } from '@ionic/angular';
import { Browser } from '@capacitor/browser';
import { FormControl } from '@angular/forms';
import { ChangeContext, LabelType, Options } from '@angular-slider/ngx-slider';
import { fromEvent, merge, Subject, Subscription, take, takeUntil } from 'rxjs';
import { ChangeDetectorRef, Component, ElementRef, NgZone, OnInit, ViewChild } from '@angular/core';
import {
  PartnershipTypeEnum,
  LoyaltySettingsType,
  InternalProductFilterType,
  ReputationWithoutTargetType,
  WalletWithReputationDtoType,
  InternalProductWithRatingsTargetPictureWithFavoriteStatusType,
  SimpleProductsForPartnerhipNetworkWithRatingsAndFavoriteStatusAndRemunerationsAndFilterType,
} from '@sifca-monorepo/widget-generator';

import { reputationPicture } from '../../environments/environment';
import { MobileAuthService, MobileReputationService } from '../../services';
import { MobileMarketPlaceService } from '../../services/marketplace.service';

@Component({
  template: '',
})
export class MobileMarketplaceComponent implements OnInit {
  @ViewChild('preloader', { static: false }) preloaderEl!: ElementRef<HTMLElement>;

  private userInteracted = false;
  private interactionSub?: Subscription;
  private preloaderObserver?: IntersectionObserver;
  protected unsubscribeAll: Subject<any> = new Subject<any>();

  apiFilter: any;
  isIos: boolean;
  toPrice: string;
  nameSort: number;
  fromPrice: string;
  priceSort: number;
  isLoading: boolean;
  isHiddenDrawer = true;
  allLimits: any[] = [];
  sliderOption: Options;
  maxValue: number = 40;
  minValue: number = 60;
  priceRange: any = [0, 100];
  selectedPartners: string[] = [];
  filter: InternalProductFilterType;
  searchString = new FormControl('');
  reputationIcon = reputationPicture;
  partners$ = this.marketService.partners$;
  currentLevel$ = this.reputationService.currentLevel$;
  productFilter: Map<number, any> = new Map<number, any>();
  loyaltySettings$ = this.reputationService.loyaltySettings$;
  loadingProducts$ = this.marketService.loadingProducts$;
  partnerLoyaltySettings = new Map<string, LoyaltySettingsType>();
  partnerWallets = new Map<string, WalletWithReputationDtoType>();
  partnerCurrentLevels = new Map<string, ReputationWithoutTargetType | null>();
  filtredProducts: SimpleProductsForPartnerhipNetworkWithRatingsAndFavoriteStatusAndRemunerationsAndFilterType[] = [];

  constructor(
    protected ngZone: NgZone,
    protected platform: Platform,
    protected cd: ChangeDetectorRef,
    protected authService: MobileAuthService,
    protected reputationService: MobileReputationService,
    protected marketService: MobileMarketPlaceService,
  ) {
    this.isIos = this.platform.is('ios');
  }

  ngOnInit(): void {
    this.marketService.filter$.pipe(takeUntil(this.unsubscribeAll)).subscribe((filter) => {
      this.sliderOption = {
        floor: parseInt(filter?.priceRange?.min, 10),
        ceil: parseInt(filter?.priceRange?.max, 10),
        getSelectionBarColor: (value: number): string => {
          return 'black';
        },
        translate: (value: number, label: LabelType): string => {
          switch (label) {
            case LabelType.Low:
              return value + ' DT';
            case LabelType.High:
              return value + ' DT';
            default:
              return value + ' DT';
          }
        },
      };
      if (filter) {
        this.filter = filter;
        const catalogueCategories = filter?.catalogueCategories;
        this.productFilter.set(1, {
          maxValue: parseInt(this.filter?.priceRange?.max, 10),
          minValue: parseInt(this.filter?.priceRange?.min, 10),
          filter: {
            ...filter,
            catalogueCategories,
          },
          priceRange: [0, +this.filter?.priceRange?.max],
          maxPrice: parseInt(this.filter?.priceRange?.max, 10),
          someRange2config: {
            behaviour: 'drag',
            connect: [true, true, true],
            step: 5,
            start: [0, 100],
            pageStep: 1,
            range: {
              min: 0,
              max: parseInt(this.filter?.priceRange?.max),
            },
          },
        });
        this.maxValue = parseInt(this.filter?.priceRange?.max, 10);
        this.minValue = parseInt(this.filter?.priceRange?.min, 10);
        forEach(this.filter?.productAttributesValues, (attribute) => {
          this.allLimits = [
            ...this.allLimits,
            {
              min: attribute.attributeValues.length < 5 ? attribute.attributeValues.length : 5,
            },
          ];
        });
        this.cd.markForCheck();
      }
    });
    this.marketService.getSimpleProductsForPartnerhipNetworkWithRatingsAndFavoriteStatusAndRemunerationsAndFilter().subscribe();
    this.marketService.filtredProducts$.pipe(takeUntil(this.unsubscribeAll)).subscribe((filtredProducts) => {
      if (filtredProducts) {
        this.filtredProducts.push(...filtredProducts.objects);
      }
      this.cd.markForCheck();
    });
  }

  ngAfterViewInit(): void {
    this.ngZone.runOutsideAngular(() => {
      const scroll$ = fromEvent(window, 'scroll');
      const wheel$ = fromEvent(window, 'wheel');
      const touch$ = fromEvent(window, 'touchstart');
      this.interactionSub = merge(scroll$, wheel$, touch$)
        .pipe(take(1))
        .subscribe(() => {
          this.userInteracted = true;
          this.setupProductsObserver();
        });
    });
  }

  getProductRewards(product: InternalProductWithRatingsTargetPictureWithFavoriteStatusType) {
    const price = this.getProductPrice(product);
    if (!price) {
      return null;
    }

    const loyaltySettings = this.getProductLoyaltySettings(product);
    if (!loyaltySettings) {
      return null;
    }

    const levelForProduct = this.getProductCurrentLevel(product);

    const rewardSource = levelForProduct
      ? this.getRemunerationForLevel(loyaltySettings, levelForProduct)
      : loyaltySettings?.prelevel?.onsiteConverter?.physical;
    if (!rewardSource) {
      return null;
    }

    const qualitativePoints = this.calculateProductPoints(rewardSource?.qualitative?.amount, price);
    const quantitativePoints = this.calculateProductPoints(rewardSource?.quantitative?.amount, price);

    if (!qualitativePoints && !quantitativePoints) {
      return null;
    }
    return {
      qualitative: qualitativePoints
        ? {
            amount: qualitativePoints,
            icon: this.getQualitativeIcon(loyaltySettings),
          }
        : null,
      quantitative: quantitativePoints
        ? {
            amount: quantitativePoints,
            icon: this.getQuantitativeIcon(rewardSource),
          }
        : null,
    };
  }

  private getProductPrice(product: InternalProductWithRatingsTargetPictureWithFavoriteStatusType): number {
    return Number(product?.barcodes?.[0]?.publicPrice) || 0;
  }

  private calculateProductPoints(rate: number | string | null | undefined, price: number): number | null {
    const numericRate = Number(rate);
    if (!numericRate || !price) {
      return null;
    }
    const points = (numericRate * price) / 100;
    return points > 0 ? Math.round(points) : null;
  }

  private getRemunerationForLevel(loyaltySettings: LoyaltySettingsType, level: ReputationWithoutTargetType | null) {
    if (!level || !loyaltySettings?.onsiteConverter?.physical?.remunerations?.length) {
      return null;
    }
    return loyaltySettings.onsiteConverter.mobile.remunerations.find(
      (remuneration) => remuneration?.reputationLevel?.reputationLevel === level.reputationLevel,
    );
  }

  private getQualitativeIcon(loyaltySettings?: LoyaltySettingsType | null): string {
    const picture = loyaltySettings?.qualitative?.picture;
    return this.buildPictureUrl(picture) || this.reputationIcon;
  }

  private getQuantitativeIcon(rewardSource: any): string {
    const picture = rewardSource?.quantitative?.wallet?.coin?.picture;
    return this.buildPictureUrl(picture) || this.reputationIcon;
  }

  private getProductLoyaltySettings(product: InternalProductWithRatingsTargetPictureWithFavoriteStatusType): LoyaltySettingsType | null {
    const posId = product?.target?.pos?.id;
    if (posId && this.partnerLoyaltySettings.has(posId)) {
      return this.partnerLoyaltySettings.get(posId) ?? null;
    }
    return null;
  }

  private getProductCurrentLevel(product: InternalProductWithRatingsTargetPictureWithFavoriteStatusType): ReputationWithoutTargetType | null {
    const posId = product?.target?.pos?.id;
    if (posId && this.partnerCurrentLevels.has(posId)) {
      return this.partnerCurrentLevels.get(posId) ?? null;
    }
    return null;
  }

  private buildPictureUrl(picture?: { baseUrl?: string; path?: string } | null): string | null {
    if (picture?.baseUrl && picture?.path) {
      return `${picture.baseUrl}/${picture.path}`;
    }
    return null;
  }

  goToProductPage(product: InternalProductWithRatingsTargetPictureWithFavoriteStatusType) {
    const website = this.getProductLoyaltySettings(product)?.target?.pos?.website;
    const url = product?.url ? product.url : `/p/${product?.name}`;
    const fullUrl = website ? (website.endsWith('/') ? website.slice(0, -1) : website) + url : url;
    Browser.open({ url: fullUrl });
  }

  loadMoreProducts() {
    this.isLoading = true;
    this.marketService.pageIndex += 1;
    this.marketService
      .getSimpleProductsForPartnerhipNetworkWithRatingsAndFavoriteStatusAndRemunerationsAndFilter(false, this.apiFilter)
      .subscribe(() => {
        this.isLoading = false;
        this.cd.markForCheck();
      });
  }

  private setupProductsObserver(): void {
    if (!this.preloaderEl) {
      setTimeout(() => this.setupProductsObserver(), 150);
      return;
    }
    if (this.preloaderObserver) this.preloaderObserver.disconnect();

    const options: IntersectionObserverInit = {
      root: null,
      threshold: 0.1,
      rootMargin: '0px 0px 150px 0px',
    };
    this.preloaderObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && this.userInteracted && !this.isLoading) {
          this.ngZone.run(() => {
            this.marketService.isLastPage$.pipe(take(1)).subscribe((isLast) => {
              if (!isLast) {
                this.loadMoreProducts();
              } else {
                this.preloaderObserver?.disconnect();
              }
            });
          });
        }
      });
    }, options);
    this.preloaderObserver.observe(this.preloaderEl.nativeElement);
  }

  openFilter() {
    this.marketService
      .getPartnershipNetworksByTargetAndPartnershipPagination(PartnershipTypeEnum.MARKETPLACE)
      .pipe(takeUntil(this.unsubscribeAll))
      .subscribe();
  }

  onChangePartners(id: string) {
    this.marketService.pageIndex = 0;
    if (this.selectedPartners.includes(id)) {
      this.selectedPartners = this.selectedPartners.filter((m) => m !== id);
    } else {
      this.selectedPartners.push(id);
    }
    this.cd.markForCheck();
  }

  applyFilter() {
    this.filtredProducts = [];
    this.marketService.searchString = '';
    this.marketService.pageIndex = 0;
    this.apiFilter = {
      partners: {
        pos: this.selectedPartners,
      },
      ...(this.fromPrice ? { fromPrice: this.fromPrice.toString() } : {}),
      ...(this.toPrice ? { toPrice: this.toPrice.toString() } : {}),
    };
    this.marketService.loadingProducts$ = true;
    this.marketService
      .getSimpleProductsForPartnerhipNetworkWithRatingsAndFavoriteStatusAndRemunerationsAndFilter(false, this.apiFilter)
      .subscribe(() => this.cd.markForCheck());
  }

  isPartnerChecked(value: string) {
    return this.selectedPartners.includes(value);
  }

  searchItems() {
    this.marketService.searchString = this.searchString.value;
    this.marketService.pageIndex = 0;
  }

  onValueChange(event: ChangeContext) {
    this.fromPrice = '' + event.value;
    this.toPrice = '' + event.highValue;
    this.priceRange = [event.value / 10, event.highValue / 10];
    this.isHiddenDrawer = true;
    this.cd.markForCheck();
  }

  ngOnDestroy() {
    this.filtredProducts = [];
    this.marketService.searchString = '';
    this.marketService.pageIndex = 0;
    this.unsubscribeAll.next(true);
    this.unsubscribeAll.complete();
    if (this.interactionSub) this.interactionSub.unsubscribe();
    if (this.preloaderObserver) this.preloaderObserver.disconnect();
  }
}
